import React, { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from 'context/auth';
import { Avatar, AvatarBadge, Box, Text, Center, Divider, Link, Heading, IconButton, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverHeader, PopoverTrigger, Stat, StatHelpText, StatLabel, StatNumber, Tooltip, useBoolean, useBreakpointValue, Flex, Spacer, Spinner, useConst } from '@chakra-ui/react';
import { BellIcon } from '@chakra-ui/icons';
import InfiniteScroll from 'react-infinite-scroll-component';

export default function NotificationPanel(props) {
    const { user } = useContext(AuthContext);
    const [isOpen, setIsOpen] = useState(false);
    const popoverOpen = useRef(false);
    const runOnce = useRef(false);
    const [notifications, setNotifications] = useState([]);
    const allNotifications = useRef([]);
    const initialNotificationsLoaded = useConst(15);
    const currentNotificationIndex = useRef(initialNotificationsLoaded);
    const [hasMore, setHasMore] = useState(true);
    const [unreadCount, setUnreadCount] = useState(0);

    const maxWidth = useBreakpointValue(
        {
          base: '300px',
          md: '450px'
        });

    const popoverOffset = useBreakpointValue(
        {
            base: [-50, 0],
            md: [-150, 0]
        });

    const timeSince = function(date) {
        if (typeof date !== 'object') {
            date = new Date(date);
        }
        
        let seconds = Math.floor((new Date().getTime() - date) / 1000);
        let intervalType;
        
        var interval = Math.floor(seconds / 31536000);
        if (interval >= 1) {
            intervalType = 'year';
        } else {
            interval = Math.floor(seconds / 2592000);
            if (interval >= 1) {
            intervalType = 'month';
            } else {
            interval = Math.floor(seconds / 86400);
            if (interval >= 1) {
                intervalType = 'day';
            } else {
                interval = Math.floor(seconds / 3600);
                if (interval >= 1) {
                intervalType = "hour";
                } else {
                interval = Math.floor(seconds / 60);
                if (interval >= 1) {
                    intervalType = "minute";
                } else {
                    interval = seconds;
                    intervalType = "second";
                }
                }
            }
            }
        }
        
        if (interval > 1 || interval === 0) {
            intervalType += 's';
        }
        
        return interval + ' ' + intervalType;
    };

    const handleMarkAsReadOnClick = (e) => {
        let tempNotifications = [...notifications];
        
        fetch(`/transactions/updateNotifications/${e._id}`, {
            method: 'GET',
        }).then(response => {
            if (response.status !== 200) {
                throw `${response.status}: ${response.statusText}`
            }
            return response.json();
        }
        ).then(data => {
            tempNotifications.find(notification => notification._id === e._id).userRead = true;
            allNotifications.current.find(notification => notification._id === e._id).userRead = true;
            setUnreadCount(allNotifications.current.filter(notification => notification.userRead === false).length);
            setNotifications(tempNotifications);
        }).catch(err => {
            console.error(err);
        });
    }

    const handleMarkAllAsReadOnClick = (e) => {
        fetch(`/transactions/updateNotifications/all`, {
            method: 'GET',
        }).then(response => {
            if (response.status !== 200) {
                throw `${response.status}: ${response.statusText}`
            }
            return response.text();
        }
        ).then(data => {
            fetch('/transactions/getNotifications', {
                method: 'GET',
            }).then(response => {
                if (response.status !== 200) {
                    throw `${response.status}: ${response.statusText}`
                }
                return response.text();          
            }).then(function(data) {
                allNotifications.current = JSON.parse(data).slice(0).reverse();
                setUnreadCount(allNotifications.current.filter(notification => notification.userRead === false).length);
                setNotifications(allNotifications.current.slice(0, initialNotificationsLoaded));
            }).catch(err => {
                console.error(err);
            });
        }).catch(err => {
            console.error(err);
        });
    }

    const fetchMoreData = () => {
        setNotifications(notifications.concat(allNotifications.current.slice(currentNotificationIndex.current, currentNotificationIndex.current + initialNotificationsLoaded)));
        currentNotificationIndex.current += initialNotificationsLoaded;
        if (currentNotificationIndex.current > allNotifications.current.length) {
            setHasMore(false);
        }
    }

    useEffect(() => {
        if (!runOnce.current) {
            fetch('/transactions/getNotifications', {
                method: 'GET',
            }).then(response => {
                if (response.status !== 200) {
                    throw `${response.status}: ${response.statusText}`
                }
                return response.text();          
            }).then(function(data) {
                allNotifications.current = JSON.parse(data).slice(0).reverse();
                setUnreadCount(allNotifications.current.filter(notification => notification.userRead === false).length);
                setNotifications(allNotifications.current.slice(0, initialNotificationsLoaded));
            }).catch(err => {
                console.error(err);
            });
            runOnce.current = true;
        }
    }, []);

    return (
        <>
            <Popover 
            // @ts-ignore
            offset={popoverOffset} onOpen={() => popoverOpen.current = true} onClose={() => popoverOpen.current = false}>
            <Tooltip
                isOpen={isOpen}
                hasArrow
                padding={2} borderRadius={12}
                aria-label="tooltip"
                placement="bottom"
                label="Notifications"
                bg='gray.300' color='black'>
                <Box display="inline-block">
                    <PopoverTrigger>
                        <IconButton
                        onMouseEnter={() => { if (!popoverOpen.current) setIsOpen(true)}}
                        onClick={() => setIsOpen(false)}
                        onMouseLeave={() => setIsOpen(false)}
                        _hover={{color: 'green.200'}}
                        _active={{bgColor: 'unset'}}
                        color='whitesmoke' variant="ghost"
                        size='lg'
                        aria-label='Notifications'
                        icon={<Avatar bgColor='unset' icon={ <BellIcon boxSize={8}/>}>
                                    {unreadCount > 0 && <AvatarBadge border={0} boxSize='1em' bg='green.500'><Text fontWeight={'light'} fontSize='2xs'>{unreadCount}</Text></AvatarBadge>}
                                </Avatar>
                                }/>
                    </PopoverTrigger>
                </Box>
            </Tooltip>
            <PopoverContent width={'inherit'}>
                <PopoverArrow />
                    <PopoverHeader>
                        <Center>
                            <Heading size='md'>Notifications</Heading>
                            <Spacer/>
                            <Link fontSize="sm" color='teal.500' onClick={handleMarkAllAsReadOnClick}>
                                                                        Mark all as read
                                                                    </Link>
                        </Center>
                    </PopoverHeader>
                <PopoverBody p={0} width={maxWidth} bgColor={'gray.100'}>
                    <Box height={"calc(50vh)"} width="100%">
                        {notifications.length > 0 ? (<InfiniteScroll
                        height={"calc(50vh)"}
                        next={fetchMoreData}
                        dataLength={notifications.length}
                        hasMore={hasMore}
                        loader={<Center><Spinner/></Center>}
                        endMessage={<Center>You've reached the end!</Center>}
                        >
                        {notifications.map((item, index) => (
                            <Box key={item._id}>
                                <Stat borderLeft={item.userRead ? "0px" : "5px solid"} padding={"10px 25px 5px"} bgColor={'white'}>
                                    <Text fontSize='sm' fontWeight={item.userRead ? 'normal' : 'bold'}>{item.message}</Text>
                                    <Flex>
                                        <StatHelpText>{timeSince(item.createdAt)} ago</StatHelpText>
                                        <Spacer/>
                                        {!item.userRead && 
                                            <Link fontSize="sm" color='teal.500' onClick={() => handleMarkAsReadOnClick(item)}>
                                                Mark as read
                                            </Link>
                                        }
                                    </Flex>
                                </Stat>
                                <Divider/>
                            </Box>
                        ))}
                        </InfiniteScroll>) : <Center height={"calc(50vh)"}>Your notifications will show up here.</Center>}
                    </Box>
                </PopoverBody>
            </PopoverContent>
            </Popover>
        </>
    );
}
